'use client';

import { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  TextInput,
  Textarea,
  Divider,
  Checkbox,
  Stack,
  Switch,
} from '@mantine/core';
import Cookies from 'js-cookie';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import styles from './EditableSliceWrapper.module.scss';
import { Dropzone } from '../EditorText/Dropzone';
import { LinkFieldInput } from './LinkFieldInput';
import { parseJwt } from '@/src/helpers/decode-jwt';
import { addDeploymentToast } from '@/src/helpers/add-deployment-tip';
import { useAuthValidation } from '@/src/lib/hooks/useAuthValidation';
import { authValidation } from '@/src/helpers/authValidation';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { AddSliceModal } from './AddSlice/AddSliceModal';
import { sliceDefinitions } from '@/src/types/sliceDefinitions';

type SliceField = {
  key: string;               // chemin (ex: "title" ou "data[0].title")
  label: string;             // label pour l’UI
  value: string | boolean;   // valeur affichée/modifiable
};

type EditableSliceWrapperProps = {
  children: React.ReactNode;
  slice: Record<string, any>;
  sliceIndex: number;
  lang: 'fr' | 'en';
  pageKey: string;
};

export const EditableSliceWrapper = ({
  children,
  slice,
  sliceIndex,
  lang,
  pageKey,
}: EditableSliceWrapperProps) => {
  const [opened, setOpened] = useState(false);
  const [addModalOpened, setAddModalOpened] = useState(false);

  // ⚠️ formValues est la source de vérité dans la modale d’édition
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [disconnected, setDisconnected] = useState(false);
  const [validated, setValidated] = useState(false);

  const router = useRouter();
  const dictionary = useDictionary();

  // ---- Session / rôles
  useEffect(() => {
    const token = Cookies.get('authToken');
    if (token) {
      try {
        const decoded = parseJwt(token);
        if (decoded?.role === 'superAdmin') {
          setIsSuperAdmin(true);
        } else if (decoded?.role === 'admin') {
          setIsAdmin(true);
        }
      } catch (err) {
        Cookies.remove('authToken');
        window.location.reload();
        console.error('Invalid token', err);
      }
    }
  }, []);

  // ---- Pré-remplir formValues : defaults du schéma + slice existante
  useEffect(() => {
    const defaults = getSchemaDefaults(slice?.type);
    // on merge pour injecter les nouveaux champs même s’ils n’existent pas dans la slice
    setFormValues({ ...defaults, ...structuredClone(slice) });
  }, [slice]);

  // ---- Champs affichés dans la modale d’édition : basés sur le schéma si dispo
  const editableFields = getEditableFieldsFromSchemaOrObject(formValues, slice?.type);

  // ---- Actions serveur
  const handleSave = async () => {
    const response = await fetch('/api/slices', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lang,
        pageKey,
        sliceIndex,
        updates: formValues,
      }),
    });

    if (response.ok) {
      addDeploymentToast('Déploiement des slices en cours. Optimisation des fichiers...');
      setOpened(false);
    } else {
      alert('Erreur lors de la sauvegarde du bloc');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Supprimer ce bloc ?')) return;
    const res = await fetch('/api/slices/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageKey,
        sliceIndex,
        locale: lang,
      }),
    });

    if (res.ok) {
      addDeploymentToast('Suppression des slices en cours. Optimisation des fichiers...');
      setOpened(false);
    } else {
      alert('Erreur lors de la suppression');
    }
  };

  const handleAdd = async (newSlice: any) => {
    const res = await fetch('/api/slices/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageKey,
        newSlice,
        locale: lang,
        position: sliceIndex + 1,
      }),
    });

    if (res.ok) {
      addDeploymentToast('Déploiement des nouvelles slices en cours. Optimisation des fichiers...');
      setAddModalOpened(false);
    } else {
      alert('Erreur lors de l’ajout du bloc');
    }
  };

  // ---- Changement d’un champ (string | boolean | any)
  const handleChange = (path: string, value: any) => {
    const updated = structuredClone(formValues);
    setDeepValue(updated, path, value);
    setFormValues(updated);
  };

  // ---- Garde : ouverture modales avec validation
  const handleOpen = async (value: boolean) => {
    const isValid = await authValidation();
    if (!isValid) {
      setDisconnected(true);
    } else {
      setValidated(true);
    }
    setOpened(value);
  };

  const handleDeleteClick = async () => {
    const isValid = await authValidation();
    if (!isValid) {
      setDisconnected(true);
    } else {
      setValidated(true);
    }
    handleDelete();
  };

  const handleOpenClick = async (value: boolean) => {
    const isValid = await authValidation();
    if (!isValid) {
      setDisconnected(true);
    } else {
      setValidated(true);
    }
    setAddModalOpened(value);
  };

  return (
    <div className={styles.EditableSliceWrapper}>
      {(isSuperAdmin || isAdmin) && (
        <div className={`${styles.buttonsUpdate} ${editableFields.length === 1 && styles.space}`}>
            {editableFields.length > 1 && (
            <Button size="xs" color="black" onClick={() => handleOpen(true)}>
              ✏️
            </Button>
          )}

          <Button size="xs" color="darkRed" onClick={handleDeleteClick}>
            🗑️
          </Button>
        </div>
      )}

      {children}

      {isSuperAdmin && (
        <>
          <div
            onClick={() => handleOpenClick(true)}
            style={{
              cursor: 'pointer',
              textAlign: 'center',
              padding: '1rem 0',
              color: '#aaa',
              userSelect: 'none',
            }}
          >
            <Divider label="+ Choisir l'élement à ajouter" labelPosition="center" />
          </div>

          <AddSliceModal
            opened={addModalOpened}
            onClose={() => setAddModalOpened(false)}
            onAdd={handleAdd}
          />
        </>
      )}

      {/* Session expirée */}
      <Modal
        opened={disconnected}
        onClose={() => {
          Cookies.remove('authToken');
          router.refresh();
        }}
        title="Session Expired"
        centered
      >
        <p>Your session has expired. Please log in again.</p>
        <Button component={Link} href="/login">
          Go to Login
        </Button>
      </Modal>

      {/* Modale d’édition */}
      <Modal
        opened={opened && !disconnected}
        onClose={() => setOpened(false)}
        title="Modifier ce bloc"
        size="lg"
        centered
      >
        <Stack>
          {editableFields.map(({ key, label, value }) => {
            const lower = key.toLowerCase();
            const isImage = (lower.includes('image') && lower !== 'isimage') || key === 'src';
            const isLink = /link|href/i.test(key);
            const isDescription = lower.includes('description');

            return (
              <div key={key} style={{ marginBottom: 16 }}>
                {/* Pour les booléens, le label est sur le Checkbox */}
                {typeof value !== 'boolean' && (
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                    {label}
                  </label>
                )}

                {typeof value === 'boolean' ? (
                  <Checkbox
                    label={label}
                    checked={value}
                    onChange={(e) => handleChange(key, e.currentTarget.checked)}
                  />
                ) : isImage ? (
                  <Dropzone
                    imageUrl={getCleanUrl(String(value))}
                    // @ts-ignore exists correctly
                    setImageUrl={(url: string) => handleChange(key, url)}
                  />
                ) : isLink ? (
                  <LinkFieldInput value={String(value)} onChange={(val) => handleChange(key, val)} lang={lang} />
                ) : key === 'version' ? (
                  <Switch
                    label="Thème sombre"
                    checked={value === 'dark'}
                    onChange={(e) => handleChange(key, e.currentTarget.checked ? 'dark' : 'light')}
                  />
                ) : isDescription ? (
                  <Textarea
                    value={String(value)}
                    onChange={(e) => handleChange(key, e.currentTarget.value)}
                    autosize
                    minRows={3}
                  />
                ) : (
                  <TextInput
                    value={String(value)}
                    onChange={(e) => handleChange(key, e.currentTarget.value)}
                  />
                )}
              </div>
            );
          })}

          <Button fullWidth mt="md" onClick={handleSave}>
            Enregistrer
          </Button>
        </Stack>
      </Modal>
    </div>
  );
};

/* =========================
   Helpers
   ========================= */

function getSchemaDefaults(type?: string) {
  if (!type) return {};
  const def: any = (sliceDefinitions as any)[type];
  if (!def?.fields) return {};
  const acc: Record<string, any> = {};
  for (const f of def.fields) {
    if (Object.prototype.hasOwnProperty.call(f, 'default')) {
      acc[f.key] = f.default;
    }
  }
  // arrayField defaults éventuels (si besoin)
  if (def?.arrayField?.default !== undefined) {
    acc[def.arrayField.key] = def.arrayField.default;
  }
  return acc;
}

/**
 * Construit la liste des champs éditables à partir du schéma si disponible.
 * Sinon, fallback sur l’ancien comportement (flattenFields(formValues)).
 */
function getEditableFieldsFromSchemaOrObject(values: any, type?: string): SliceField[] {
  const def: any = type ? (sliceDefinitions as any)[type] : undefined;

  if (def?.fields?.length) {
    const list: SliceField[] = [];

    for (const f of def.fields) {
      const raw = getDeepValue(values, f.key);
      const v =
        raw === undefined
          ? Object.prototype.hasOwnProperty.call(f, 'default')
            ? f.default
            : f.type === 'boolean'
            ? false
            : ''
          : raw;

      list.push({
        key: f.key,
        label: f.label || f.key,
        value: typeof v === 'string' ? getCleanUrl(v) : v,
      });
    }

    // Note : si tu veux éditer des arrayField ici, ajoute un rendu dédié
    return list;
  }

  // Fallback : pas de schéma → on aplatit l’objet existant
  return flattenFields(values);
}

/**
 * Aplati un objet en liste de paires (clé chemin / valeur) pour affichage.
 * Conserve ton comportement legacy.
 */
function flattenFields(obj: any, prefix = ''): SliceField[] {
  const fields: SliceField[] = [];

  Object.entries(obj ?? {}).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      fields.push({
        key: path,
        label: key,
        value: getCleanUrl(value),
      });
    } else if (typeof value === 'boolean') {
      fields.push({
        key: path,
        label: key,
        value: value,
      });
    } else if (Array.isArray(value)) {
      if (value.every((item) => typeof item === 'string')) {
        value.forEach((item, index) => {
          fields.push({
            key: `${path}[${index}]`,
            label: `${key} ${index + 1}`,
            value: getCleanUrl(item),
          });
        });
      } else {
        value.forEach((item, index) => {
          fields.push(...flattenFields(item, `${path}[${index}]`));
        });
      }
    } else if (typeof value === 'object' && value !== null) {
      fields.push(...flattenFields(value, path));
    }
  });

  return fields;
}

/** Nettoie les URL proxifiées pour l’affichage */
function getCleanUrl(value: string): string {
  try {
    // @ts-ignore
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const url = new URL(value, base || 'http://local');
    if (url.pathname === '/api/image-proxy' && url.searchParams.has('url')) {
      return decodeURIComponent(url.searchParams.get('url') || '');
    }
  } catch {
    // ignore
  }
  return value;
}

/** set profondément une valeur via un chemin "a.b[0].c" */
function setDeepValue(obj: any, path: string, value: any): void {
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (!(k in current) || typeof current[k] !== 'object' || current[k] === null) {
      // crée un objet pour poursuivre (simple ; adapter si besoin pour array)
      current[k] = {};
    }
    current = current[k];
  }
  current[keys[keys.length - 1]] = value;
}

function getDeepValue(obj: any, path: string): any {
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  return keys.reduce((acc, key) => acc?.[key], obj);
}
